import {
	DiffLinePopoverContent,
	DiffLinePopoverHeader,
	DiscardButton,
} from "@ext/git/core/Diff/components/DiffLine/DiffLinePopover";
import { COLUMN_MARKER_CLASS, isInlineWidget, TOP_LEVEL_DELETED } from "@ext/git/core/Diff/logic/collapseDeleted";
import { fitRowsToTable, restoreDeletedColumn } from "@ext/git/core/Diff/logic/deletedColumns";
import { useHoverPopover } from "@ext/git/core/Diff/logic/hooks/useHoverPopover";
import { restoreDeleted } from "@ext/git/core/Diff/logic/restoreChange";
import t from "@ext/localization/locale/translate";
import type { DiffChange } from "@gaurussel/tiptap-diff-utility";
import { Popover, PopoverAnchor } from "@ui-kit/Popover";
import { TooltipProvider } from "@ui-kit/Tooltip";
import type { Node } from "prosemirror-model";
import { Plugin, PluginKey } from "prosemirror-state";
import type { EditorView } from "prosemirror-view";
import { createRoot, type Root } from "react-dom/client";

export const deletedRestoreKey = new PluginKey("diff-deleted-restore");

const HOST_CLASS = "diff-deleted-restore";

const VOID_BLOCKS = new Set(["HR", "IMG", "BR"]);

const hostOwner = (block: HTMLElement): HTMLElement | null => {
	if (VOID_BLOCKS.has(block.tagName)) return null;
	if (block.tagName === "TR") return block.querySelector<HTMLElement>(":scope > td, :scope > th");
	return block;
};

const DeletedBlockPopover = ({ label, onRestore }: { label: string; onRestore: () => void }) => {
	const hover = useHoverPopover();
	return (
		<TooltipProvider>
			<Popover onOpenChange={hover.setOpen} open={hover.open}>
				<PopoverAnchor asChild>
					<span aria-label={label} className="block h-full w-full" role="img" {...hover.triggerProps} />
				</PopoverAnchor>
				<DiffLinePopoverContent className="w-auto overflow-hidden p-0 pb-2" {...hover.contentProps}>
					<DiffLinePopoverHeader
						action={
							<DiscardButton
								onClick={() => {
									hover.close();
									onRestore();
								}}
								qa="diff-deleted-restore"
							/>
						}
						title={t("diff.type.deleted")}
					/>
				</DiffLinePopoverContent>
			</Popover>
		</TooltipProvider>
	);
};

const blockPos = (view: EditorView, block: HTMLElement) => {
	try {
		return view.posAtDOM(block, 0);
	} catch {
		return undefined;
	}
};

export const deletedNodesByBlock = (view: EditorView, changes: DiffChange[]): Map<HTMLElement, Node[]> => {
	const blocksAt = new Map<number, HTMLElement[]>();
	view.dom.querySelectorAll<HTMLElement>(TOP_LEVEL_DELETED).forEach((block) => {
		if (isInlineWidget(block)) return;
		const pos = blockPos(view, block);
		if (pos === undefined) return;
		blocksAt.set(pos, [...(blocksAt.get(pos) ?? []), block]);
	});

	const result = new Map<HTMLElement, Node[]>();
	blocksAt.forEach((blocks, pos) => {
		const nodes = changes
			.filter((change) => change.type === "deleted" && change.from === pos && change.nodeA)
			.sort((left, right) => (left.posA ?? 0) - (right.posA ?? 0))
			.map((change) => change.nodeA as Node);
		if (!nodes.length) return;
		if (blocks.length === nodes.length) blocks.forEach((block, index) => result.set(block, [nodes[index]]));
		else if (blocks.length === 1) result.set(blocks[0], nodes);
	});
	return result;
};

const COLUMN_HOST_CLASS = "diff-column-restore";

const tableRangeOf = (view: EditorView, cell: HTMLElement): { from: number; to: number } | undefined => {
	const row = cell.closest("tr");
	if (!row) return undefined;
	try {
		const Pos = view.state.doc.resolve(view.posAtDOM(row, 0));
		for (let depth = Pos.depth; depth > 0; depth--) {
			if (Pos.node(depth).type.spec.tableRole === "table")
				return { from: Pos.before(depth), to: Pos.after(depth) };
		}
	} catch {
		return undefined;
	}
	return undefined;
};

const deletedRestore = (getChanges: () => DiffChange[] | undefined) =>
	new Plugin({
		key: deletedRestoreKey,
		view: (view) => {
			const roots = new Map<HTMLElement, Root>();

			const release = (host: HTMLElement) => {
				const root = roots.get(host);
				roots.delete(host);
				if (root) setTimeout(() => root.unmount(), 0);
			};

			const refresh = (target: EditorView) => {
				if (!target.editable) {
					[...roots.keys()].forEach(release);
					return;
				}
				roots.forEach((_, host) => {
					if (!host.isConnected) release(host);
				});

				const changes = getChanges() ?? [];
				const mount = (owner: HTMLElement, className: string, label: string, onRestore: () => void) => {
					let host = owner.querySelector<HTMLElement>(`:scope > .${className}`);
					if (!host) {
						host = document.createElement("span");
						host.className = className;
						host.contentEditable = "false";
						owner.appendChild(host);
					}
					const root = roots.get(host) ?? createRoot(host);
					roots.set(host, root);
					root.render(<DeletedBlockPopover label={label} onRestore={onRestore} />);
				};

				target.dom.querySelectorAll<HTMLElement>(`.${COLUMN_MARKER_CLASS}`).forEach((marker) => {
					const cell = marker.closest<HTMLTableCellElement>("td, th");
					const table = cell && tableRangeOf(target, cell);
					if (!cell || !table) return;
					const column = cell.cellIndex;
					if (!restoreDeletedColumn(target.state, changes, table, column)) return;
					mount(marker, COLUMN_HOST_CLASS, t("diff.deleted.column"), () => {
						if (!target.editable) return;
						const tr = restoreDeletedColumn(target.state, getChanges() ?? [], table, column);
						if (tr) target.dispatch(tr);
					});
				});

				if (!changes.some((change) => change.type === "deleted")) return;

				deletedNodesByBlock(target, changes).forEach((deleted, block) => {
					const at = blockPos(target, block);
					if (at === undefined) return;
					const nodes = fitRowsToTable(target.state, changes, at, deleted);
					if (!restoreDeleted(target.state, at, nodes)) return;

					const owner = hostOwner(block);
					if (!owner) return;
					mount(owner, HOST_CLASS, t("diff.type.deleted"), () => {
						if (!target.editable) return;
						const tr = restoreDeleted(target.state, at, nodes);
						if (tr) target.dispatch(tr);
					});
				});
			};

			refresh(view);
			return {
				update: (updatedView) => refresh(updatedView),
				destroy: () => {
					[...roots.keys()].forEach(release);
				},
			};
		},
	});

export default deletedRestore;
